import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  get,
  getAll,
  create,
  deleteWaterfall,
  update,
  createVersion,
  updateVersion,
  deleteVersion,
  setDisabledStatus,
  setCurrentVersion,
} from '../services/waterfallService';
import {
  CreateOrUpdateWaterfallVersionViewModel,
  CreateOrUpdateWaterfallViewModel,
  WaterfallViewModel,
} from '../models/waterfall';
import { cloneStepWithNewGuids } from '../pages/waterfall-editor/stepUtils';

export const useWaterfalls = (id?: number, includeVersions = false) => {
  const queryClient = useQueryClient();

  const waterfallsQuery = useQuery<WaterfallViewModel[]>({
    queryKey: ['waterfalls', { includeVersions }],
    queryFn: () => getAll(includeVersions),
    staleTime: 30_000,
  });

  const waterfallDetailQuery = useQuery<WaterfallViewModel>({
    queryKey: ['waterfall', id],
    queryFn: () => get(id!),
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallViewModel) => create(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', created.waterfallId], created);
    },
  });

  const updateWaterfallMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallViewModel) => update(payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', updated.waterfallId], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteWaterfall(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.removeQueries({ queryKey: ['waterfall', id] });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallVersionViewModel) => {
      if (!payload.waterfallId) {
        throw new Error('waterfallId is required');
      }
      return createVersion(payload.waterfallId, payload);
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['waterfall', payload.waterfallId] });
    },
  });

  const updateVersionMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallVersionViewModel) => {
      if (!payload.waterfallId) {
        throw new Error('waterfallId is required');
      }
      return updateVersion(payload.waterfallId, payload);
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['waterfall', payload.waterfallId] });
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: ({ waterfallId, waterfallVersionId }: { waterfallId: number; waterfallVersionId: number }) =>
      deleteVersion(waterfallId, waterfallVersionId),
    onSuccess: (_, { waterfallId }) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.invalidateQueries({ queryKey: ['waterfall', waterfallId] });
    },
  });

  const cloneMutation = useMutation({
    mutationFn: async ({ sourceId, name, description }: { sourceId: number; name: string; description: string }) => {
      const source = await get(sourceId, true);
      const created = await create({
        name,
        description,
        integrationType: source.integrationType,
      });

      const sourceVersion =
        source.currentVersion ??
        source.versions?.find((v) => v.waterfallVersionId === source.currentVersionId) ??
        (source.versions?.length
          ? [...source.versions].sort((a, b) => b.waterfallVersionId - a.waterfallVersionId)[0]
          : undefined);

      if (sourceVersion) {
        const newVersion = await createVersion(created.waterfallId, {
          waterfallId: created.waterfallId,
          executionMode: sourceVersion.executionMode,
          initialSteps: sourceVersion.initialSteps?.map(cloneStepWithNewGuids) ?? null,
          initialStepsExecutionMode: sourceVersion.initialStepsExecutionMode,
          refreshOrderSettings: sourceVersion.refreshOrderSettings ?? null,
          alwaysSendParentWaterfallDetailInWebhookEvents: sourceVersion.alwaysSendParentWaterfallDetailInWebhookEvents,
        });
        return setCurrentVersion(created.waterfallId, newVersion.waterfallVersionId);
      }

      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', created.waterfallId], created);
    },
  });

  const setDisabledStatusMutation = useMutation({
    mutationFn: ({ waterfallId, disabled }: { waterfallId: number; disabled: boolean }) =>
      setDisabledStatus(waterfallId, disabled),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', updated.waterfallId], updated);
    },
  });

  return {
    waterfallsQuery,
    waterfallDetailQuery,
    createWaterfall: createMutation.mutateAsync,
    cloneWaterfall: cloneMutation.mutateAsync,
    updateWaterfall: updateWaterfallMutation.mutateAsync,
    deleteWaterfall: deleteMutation.mutateAsync,
    createVersion: createVersionMutation.mutateAsync,
    updateVersion: updateVersionMutation.mutateAsync,
    deleteVersion: deleteVersionMutation.mutateAsync,
    setDisabledStatus: setDisabledStatusMutation.mutateAsync,
  };
};
